/**
 * Serviço de cache inteligente para otimizar leituras do Firestore
 * Mantém dados no localStorage e sincroniza com backend apenas quando necessário
 * Cache isolado por usuário para evitar contaminação de dados
 */
export class CacheService {
  /** TTL padrão de 10 minutos em milissegundos (600.000ms) */
  static DEFAULT_TTL = 10 * 60 * 1000

  constructor() {
    this.prefix = 'equilibra_cache_'
    this.currentUserUid = null
  }

  /**
   * Define o UID do usuário atual para isolamento de cache
   * @param {string} uid - UID do usuário Firebase
   */
  setCurrentUser(uid) {
    this.currentUserUid = uid
  }

  /**
   * Gera chave de cache com isolamento por usuário
   * @param {string} key - Chave base
   * @returns {string} Chave com UID do usuário
   */
  _getUserKey(key) {
    if (!this.currentUserUid) {
      console.warn('⚠️ Cache sem usuário definido')
      return this.prefix + key
    }
    return `${this.prefix}${this.currentUserUid}_${key}`
  }

  /**
   * Verifica se o cache de uma chave está válido
   * Centraliza a lógica de verificação: existência + UID + idade
   * Substitui os métodos _isCacheValid() individuais de cada cache service
   * @param {string} key - Chave do cache (sem prefixo/UID)
   * @param {number} [ttl=CacheService.DEFAULT_TTL] - TTL em ms (padrão 10 min)
   * @returns {boolean} True se cache existe, pertence ao usuário atual e está dentro do TTL
   */
  isValid(key, ttl = CacheService.DEFAULT_TTL) {
    if (!this.currentUserUid) return false

    try {
      const userKey = this._getUserKey(key)
      const cached = localStorage.getItem(userKey)
      if (!cached) return false

      const cacheData = JSON.parse(cached)

      // Verifica pertencimento ao usuário atual
      if (cacheData.uid && cacheData.uid !== this.currentUserUid) return false

      // Verifica idade do cache contra TTL
      const cacheAge = Date.now() - cacheData.timestamp
      return cacheAge < ttl
    } catch (error) {
      return false
    }
  }

  /**
   * Salva dados no cache (localStorage) isolado por usuário
   * @param {string} key - Chave do cache
   * @param {Array|Object} data - Dados para cachear
   */
  set(key, data) {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        version: '2.0',
        uid: this.currentUserUid
      }
      localStorage.setItem(this._getUserKey(key), JSON.stringify(cacheData))
    } catch (error) {
      console.warn('Erro ao salvar cache:', error)
    }
  }

  /**
   * Recupera dados do cache isolado por usuário
   * @param {string} key - Chave do cache
   * @returns {Array|Object|null} Dados do cache ou null
   */
  get(key) {
    try {
      const cached = localStorage.getItem(this._getUserKey(key))
      if (!cached) return null

      const cacheData = JSON.parse(cached)
      
      // Verifica se cache pertence ao usuário atual
      if (cacheData.uid && cacheData.uid !== this.currentUserUid) {
        this.remove(key) // Remove cache de outro usuário
        return null
      }
      
      return cacheData.data
    } catch (error) {
      console.warn('Erro ao ler cache:', error)
      return null
    }
  }

  /**
   * Verifica se existe cache para uma chave do usuário atual
   * @param {string} key - Chave do cache
   * @returns {boolean} True se existe cache
   */
  has(key) {
    return localStorage.getItem(this._getUserKey(key)) !== null
  }

  /**
   * Remove cache específico do usuário atual
   * @param {string} key - Chave do cache
   */
  remove(key) {
    localStorage.removeItem(this._getUserKey(key))
  }

  /**
   * Limpa todo o cache do usuário atual
   */
  clear() {
    if (!this.currentUserUid) return
    
    const userPrefix = `${this.prefix}${this.currentUserUid}_`
    Object.keys(localStorage)
      .filter(key => key.startsWith(userPrefix))
      .forEach(key => localStorage.removeItem(key))
  }

  /**
   * Limpa cache de todos os usuários (usado em casos extremos)
   */
  clearAll() {
    Object.keys(localStorage)
      .filter(key => key.startsWith(this.prefix))
      .forEach(key => localStorage.removeItem(key))
  }

  /**
   * Adiciona item ao cache de array
   * @param {string} key - Chave do cache
   * @param {Object} item - Item para adicionar
   */
  addToArray(key, item) {
    const cached = this.get(key) || []
    cached.push(item)
    this.set(key, cached)
  }

  /**
   * Atualiza item no cache de array
   * @param {string} key - Chave do cache
   * @param {string} itemId - ID do item
   * @param {Object} updatedItem - Item atualizado
   */
  updateInArray(key, itemId, updatedItem) {
    const cached = this.get(key) || []
    const index = cached.findIndex(item => item && item.id === itemId)
    if (index >= 0) {
      cached[index] = updatedItem
      this.set(key, cached)
    }
  }

  /**
   * Remove item do cache de array
   * @param {string} key - Chave do cache
   * @param {string} itemId - ID do item
   */
  removeFromArray(key, itemId) {
    const cached = this.get(key) || []
    const filtered = cached.filter(item => item && item.id !== itemId)
    this.set(key, filtered)
  }
}

// Instância singleton
export const cacheService = new CacheService()